import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Plus, Minus } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";

export function OfferingCard({ offering }) {
  return (
    <motion.article
      whileHover={{ y: -6 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`offering-card-${offering.id}`}
      className="group relative glass rounded-3xl p-8 h-full flex flex-col overflow-hidden transition-colors duration-500 hover:border-gold-400/35"
    >
      <div className="absolute -top-10 -right-4 font-mono text-[88px] font-semibold text-gold-400/[0.07] leading-none pointer-events-none group-hover:text-gold-400/[0.14] transition-colors duration-500">
        {offering.index}
      </div>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold-400/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="flex flex-wrap gap-2 mb-6">
        {offering.badges.map((b) => (
          <span
            key={b}
            className="px-2.5 py-1 rounded-full font-mono text-[9px] uppercase tracking-[0.16em] text-gold-200/90 border border-gold-400/25 bg-gold-400/[0.06]"
          >
            {b}
          </span>
        ))}
      </div>

      <h3 className="font-display text-2xl font-bold tracking-tight text-ink-50 mb-3">
        {offering.title}
      </h3>
      <p className="text-sm text-zinc-400 leading-relaxed mb-6">{offering.description}</p>

      <ul className="flex flex-col gap-2.5 mb-8">
        {offering.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-[13px] text-zinc-300">
            <Plus size={13} className="mt-0.5 text-gold-400 shrink-0" />
            {f}
          </li>
        ))}
      </ul>

      <Link
        to={offering.cta.to}
        data-testid={`offering-cta-${offering.id}`}
        className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-gold-300 hover:text-gold-200 transition-colors group/cta w-fit"
      >
        {offering.cta.label}
        <ArrowRight
          size={15}
          className="transition-transform duration-300 group-hover/cta:translate-x-1.5"
        />
      </Link>
    </motion.article>
  );
}

/* ---- FAQ (details/summary, gold-styled) ---- */

export function FAQ({ items, idPrefix = "faq" }) {
  return (
    <div className="flex flex-col gap-3" data-testid="faq-list">
      {items.map((item, i) => (
        <details
          key={i}
          data-testid={`${idPrefix}-item-${i}`}
          className="group glass rounded-2xl overflow-hidden [&[open]]:border-gold-400/30"
        >
          <summary
            data-testid={`${idPrefix}-trigger-${i}`}
            className="flex items-center justify-between gap-4 px-6 py-5 cursor-pointer list-none [&::-webkit-details-marker]:hidden"
          >
            <span className="text-[15px] font-medium text-zinc-100 group-open:text-gold-200 transition-colors">
              {item.q}
            </span>
            <span className="shrink-0 text-gold-400 group-open:hidden">
              <Plus size={16} />
            </span>
            <span className="shrink-0 text-gold-400 hidden group-open:inline">
              <Minus size={16} />
            </span>
          </summary>
          <div className="px-6 pb-6 -mt-1">
            <p className="text-sm text-zinc-400 leading-relaxed max-w-3xl">{item.a}</p>
          </div>
        </details>
      ))}
    </div>
  );
}

/* ---- Compact illustrative chart (offering pages) ---- */

const ILLUSTRATIVE = [
  { m: "M1", v: 100 }, { m: "M2", v: 104 }, { m: "M3", v: 99 }, { m: "M4", v: 108 },
  { m: "M5", v: 114 }, { m: "M6", v: 110 }, { m: "M7", v: 120 }, { m: "M8", v: 117 },
  { m: "M9", v: 128 }, { m: "M10", v: 124 }, { m: "M11", v: 134 }, { m: "M12", v: 141 },
];

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass rounded-lg px-3 py-2 text-xs font-mono text-gold-200 border border-gold-400/30">
      Index {payload[0].value}
    </div>
  );
}

export function IllustrativeChartCard({ testid = "illustrative-chart" }) {
  return (
    <div data-testid={testid} className="glass rounded-3xl p-6 sm:p-8">
      <div className="mb-6 rounded-lg border border-gold-400/35 bg-gold-400/[0.08] px-4 py-2.5">
        <p className="font-mono text-[10px] sm:text-[11px] tracking-[0.18em] uppercase text-gold-200 text-center">
          Illustrative example — not actual performance
        </p>
      </div>
      <div className="h-[220px]" aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={ILLUSTRATIVE} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
            <defs>
              <linearGradient id="goldFillCompact" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#FFC107" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#FFC107" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="m" tick={{ fill: "#52525B", fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
            <YAxis hide domain={[90, 150]} />
            <Tooltip content={<ChartTooltip />} cursor={{ stroke: "rgba(255,193,7,0.3)" }} />
            <Area type="monotone" dataKey="v" stroke="#FFC107" strokeWidth={2} fill="url(#goldFillCompact)" dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-4 text-xs text-zinc-500 leading-relaxed">
        Schematic curve shown solely to demonstrate how verified performance will be presented once
        available. It represents no product, period or return.
      </p>
    </div>
  );
}